import React from "react";
import profilephoto from "../../assets/images/Ellipse.png";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "antd";
import { getStatusDisplayText } from "../../utils/statusUtils";

const CustomerCard = ({
  data,
  onUnsubscribe,
  sourcePage = "/dashboard",
  showViewBtn = true,
}) => {
  let navigate = useNavigate();

  const calculateRiskReward = () => {
    const entry = parseFloat(data.entryPriceLower);
    const stoploss = parseFloat(data.stoploss);
    const target = parseFloat(data.targetPrice);

    if (entry && stoploss && target) {
      const risk = Math.abs(entry - stoploss);
      const reward = Math.abs(target - entry);

      if (risk === 0) return "— : —";

      const ratio = reward / risk;
      return `1 : ${ratio.toFixed(2)}`;
    }
    return "— : —";
  };

  const riskRewardRatio = calculateRiskReward();

  return (
    <div className="w-full px-2 sm:px-4">
      <div className="relative flex flex-col rounded-[8px] border border-[#E5E5E5] bg-white font-inter w-full shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-md transition-shadow">
        {data.segmentTag && (
          <div className="px-4 sm:px-6 pt-4">
            <div className="inline-flex items-center gap-2">
              <div className="text-primary bg-brand-lightBlue text-center font-inter text-xs font-medium rounded-md flex py-1 px-3 whitespace-nowrap">
                {data.segmentTag}
              </div>
            </div>
          </div>
        )}

        {onUnsubscribe && (
          <div className="absolute top-0 right-0 z-10">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onUnsubscribe();
              }}
              className="p-3 text-gray-400 hover:text-red-500 transition-colors"
              title="Unsubscribe"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}

        <div className="px-4 sm:px-6 pt-4 pb-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <img
                src={profilephoto}
                alt="profile"
                className="w-10 h-10 rounded-full object-cover border border-gray-100"
              />

              <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-wrap sm:flex-nowrap">
                <Tooltip placement="bottomLeft" title={data.tradingSymbol}>
                  <h3 className="text-lg sm:text-xl font-bold text-[#454545] leading-snug font-inter min-w-0 w-auto whitespace-normal break-words sm:w-[240px] lg:w-[300px] sm:truncate sm:whitespace-nowrap">
                    {data.tradingSymbol}
                  </h3>
                </Tooltip>

                <div
                  className={`px-2.5 py-1 rounded-md text-white font-bold text-xs tracking-wide text-center w-14 shrink-0 ${
                    data?.transactionType === "SELL"
                      ? "bg-brand-red"
                      : "bg-[#119b42]"
                  }`}
                >
                  {data.transactionType}
                </div>

                <div className="hidden sm:flex items-center justify-end gap-3 text-sm text-[#7F7F7F] min-w-0">
                  <div className="flex items-center gap-2 min-w-0 w-[280px]">
                    <span className="shrink-0">Status</span>
                    <Tooltip
                      title={getStatusDisplayText(
                        data.status,
                        data.percentToTrigger
                      )}
                    >
                      <span className="font-bold text-[#454545] truncate whitespace-nowrap">
                        {getStatusDisplayText(
                          data.status,
                          data.percentToTrigger
                        )}
                      </span>
                    </Tooltip>
                  </div>

                  <span className="text-gray-300 shrink-0">•</span>

                  <div className="flex items-center gap-2 whitespace-nowrap">
                    <span>Confidence</span>
                    <span className="font-bold text-[#454545]">
                      {data.confidence}%
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 w-full justify-start sm:w-auto sm:justify-end flex-wrap lg:flex-nowrap lg:shrink-0">
              {showViewBtn && (
                <button
                  onClick={() =>
                    navigate(`/summary/${data.id}`, {
                      state: { ...data, from: sourcePage },
                    })
                  }
                  className="w-full sm:w-auto px-4 sm:px-6 py-2 bg-[#1669AA] text-white rounded-lg font-medium hover:bg-[#1669AA]/90 transition-colors text-sm sm:text-base whitespace-nowrap"
                >
                  View Call
                </button>
              )}
            </div>

            <div className="sm:hidden flex items-center justify-center gap-3 text-xs text-[#7F7F7F] flex-wrap">
              <div className="flex items-center gap-2 min-w-0 w-[180px]">
                <span className="shrink-0">Status</span>
                <Tooltip
                  title={getStatusDisplayText(
                    data.status,
                    data.percentToTrigger
                  )}
                >
                  <span className="font-bold text-[#454545] truncate whitespace-nowrap">
                    {getStatusDisplayText(data.status, data.percentToTrigger)}
                  </span>
                </Tooltip>
              </div>

              <span className="text-gray-300 shrink-0">•</span>

              <div className="flex items-center gap-2 whitespace-nowrap w-[70px] shrink-0 justify-end">
                <span className="shrink-0 ml-1">Confidence</span>
                <span className="font-bold text-[#454545]">
                  {data.confidence}%
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-100">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-4 px-4 sm:px-6 py-4">
            <div className="flex flex-col items-center justify-center px-2 py-2">
              <Tooltip title={data.price ?? "-"}>
                <span className="text-sm sm:text-base lg:text-lg xl:text-lg font-bold text-[#454545] break-words text-center">
                  {data.price ?? "-"}
                </span>
              </Tooltip>
              <span className="text-xs sm:text-sm lg:text-base xl:text-base text-[#7F7F7F] font-medium mt-1">
                Current Price
              </span>
            </div>

            <div className="flex flex-col items-center justify-center px-2 py-2">
              <Tooltip
                title={
                  data.entryPriceLower !== null && data.entryPriceUpper !== null
                    ? `${data.entryPriceLower}-${data.entryPriceUpper}`
                    : "-"
                }
              >
                <span className="text-sm sm:text-base lg:text-lg xl:text-lg font-bold text-[#454545] break-words text-center">
                  {data.entryPriceLower !== null &&
                  data.entryPriceUpper !== null
                    ? `${data.entryPriceLower}-${data.entryPriceUpper}`
                    : "-"}
                </span>
              </Tooltip>
              <span className="text-xs sm:text-sm lg:text-base xl:text-base text-[#7F7F7F] font-medium mt-1">
                Entry Price Range
              </span>
            </div>

            <div className="flex flex-col items-center justify-center px-2 py-2">
              <span className="text-sm sm:text-base lg:text-lg xl:text-lg font-bold text-[#454545] break-words text-center">
                {data.duration} Days
              </span>
              <span className="text-xs sm:text-sm lg:text-base xl:text-base text-[#7F7F7F] font-medium mt-1">
                Duration
              </span>
            </div>

            <div className="flex flex-col items-center justify-center px-2 py-2">
              <Tooltip
                title={
                  data.maximumProfitPercentage !== undefined &&
                  data.maximumProfitPercentage !== null
                    ? `${Math.round(data.maximumProfitPercentage)}%`
                    : "-"
                }
              >
                <span className="text-sm sm:text-base lg:text-lg xl:text-lg font-bold text-[#454545] break-words text-center">
                  {data.maximumProfitPercentage !== undefined &&
                  data.maximumProfitPercentage !== null
                    ? `${Math.round(data.maximumProfitPercentage)}%`
                    : "-"}
                </span>
              </Tooltip>
              <span className="text-xs sm:text-sm lg:text-base xl:text-base text-[#7F7F7F] font-medium mt-1">
                Max Profit
              </span>
            </div>

            <div className="flex flex-col items-center justify-center px-2 py-2">
              <Tooltip
                title={
                  data.minimumLossPercentage !== undefined &&
                  data.minimumLossPercentage !== null
                    ? `${Math.round(data.minimumLossPercentage)}%`
                    : "-"
                }
              >
                <span className="text-sm sm:text-base lg:text-lg xl:text-lg font-bold text-[#454545] break-words text-center">
                  {data.minimumLossPercentage !== undefined &&
                  data.minimumLossPercentage !== null
                    ? `${Math.round(data.minimumLossPercentage)}%`
                    : "-"}
                </span>
              </Tooltip>
              <span className="text-xs sm:text-sm lg:text-base xl:text-base text-[#7F7F7F] font-medium mt-1">
                Max Loss
              </span>
            </div>

            <div className="flex flex-col items-center justify-center px-2 py-2">
              <span className="text-sm sm:text-base lg:text-lg xl:text-lg font-bold text-[#454545] break-words text-center">
                {riskRewardRatio || "-"}
              </span>
              <span className="text-xs sm:text-sm lg:text-base xl:text-base text-[#7F7F7F] font-medium mt-1">
                Risk Reward Ratio
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerCard;
